import { APIFieldType, Operator } from '@ardoq/api-types';
import { isExclusivelyPerspectivesOperator } from '../types';

const apiFieldTypeToOperatorLookupTable = {
  [APIFieldType.CHECKBOX]: [
    Operator.CHECKED,
    Operator.UNCHECKED,
    Operator.UNSET,
  ],
  [APIFieldType.DATE_ONLY_RANGE]: [Operator.LESS, Operator.GREATER],
  [APIFieldType.DATE_TIME_RANGE]: [Operator.LESS, Operator.GREATER],
  [APIFieldType.DATE_ONLY]: [
    Operator.EQUAL,
    Operator.GREATER,
    Operator.LESS,
    Operator.BETWEEN,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
  [APIFieldType.DATE_TIME]: [
    Operator.EQUAL,
    Operator.GREATER,
    Operator.LESS,
    Operator.BETWEEN,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
  [APIFieldType.EMAIL]: [
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
  [APIFieldType.NUMBER]: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.GREATER,
    Operator.LESS,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
  [APIFieldType.LIST]: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
  [APIFieldType.SELECT_MULTIPLE_LIST]: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
  [APIFieldType.TEXT]: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
  [APIFieldType.TEXT_AREA]: [
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
  [APIFieldType.URL]: [
    Operator.CONTAINS_SUBSTRING,
    Operator.NOT_CONTAINS_SUBSTRING,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
  [APIFieldType.USER]: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
  ],
  [APIFieldType.FILE]: [Operator.IS_EMPTY, Operator.IS_NOT_EMPTY],
} as const satisfies Record<APIFieldType, Operator[]>;

export type AdvancedSearchOperators = {
  [K in keyof typeof apiFieldTypeToOperatorLookupTable]: Exclude<
    (typeof apiFieldTypeToOperatorLookupTable)[K][number],
    // Exclude the same operators as `getAdvacedSearchOperators` does through `isExclusivelyPerspectivesOperator`
    | Operator.CONTAINS
    | Operator.NOT_CONTAINS
    | Operator.DO_NOT_USE_CONTAINS_PHRASE
    | Operator.DO_NOT_USE_NOT_CONTAINS_PHRASE
  >;
};

export const getAdvancedSearchOperators = <FieldType extends APIFieldType>(
  fieldType: FieldType
): AdvancedSearchOperators[FieldType][] => {
  const operators = apiFieldTypeToOperatorLookupTable[fieldType];
  return operators.filter(
    operator => !isExclusivelyPerspectivesOperator(operator)
  ) as AdvancedSearchOperators[FieldType][];
};

export const getOperators = (fieldType: APIFieldType): Operator[] => [
  ...apiFieldTypeToOperatorLookupTable[fieldType],
];
